import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { FileText, Loader2 } from 'lucide-react'
import { createBlankProject } from '@/api/workbench'
import { useProjectStore } from '@/store/projectStore'

// Lowercase-friendly slug used to derive a Project ID from a free-text
// title. Spaces collapse to underscores; anything outside the safe set
// is dropped. Mirrors the backend's `[A-Za-z0-9_-]+` validation.
const slugify = (s: string): string =>
  s.trim().replace(/\s+/g, '_').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64)

interface StartBlankFormProps {
  /** Disable the form while a surrounding flow is busy (e.g. FRS generation). */
  disabled?: boolean
}

/**
 * "Start blank" — the non-AI path. Creates a named project with an empty
 * navigation tree so power users can jump straight into the Workbench
 * editors and add Transactions, Smart Pages, and Dashboards manually.
 * Shared by the FRS builder page (/generateapp) and the Builder hub's
 * "Let me build myself" advanced option.
 */
export default function StartBlankForm({ disabled = false }: StartBlankFormProps) {
  const navigate = useNavigate()
  const { fetchProjects, selectProject } = useProjectStore()

  // Project ID auto-derives from Title on first blur but remains
  // user-editable thereafter.
  const [blankTitle, setBlankTitle] = useState('')
  const [blankId, setBlankId] = useState('')
  const [blankIdTouched, setBlankIdTouched] = useState(false)
  const [creatingBlank, setCreatingBlank] = useState(false)
  const blankIdValid = /^[A-Za-z0-9_-]+$/.test(blankId)

  const handleCreateBlank = useCallback(async () => {
    const title = blankTitle.trim()
    const id = blankId.trim()
    if (!title) {
      toast.error('Title is required')
      return
    }
    if (!id || !/^[A-Za-z0-9_-]+$/.test(id)) {
      toast.error('Project ID must be letters, digits, _ or -')
      return
    }
    setCreatingBlank(true)
    try {
      const result = await createBlankProject(id, title)
      toast.success('Project created')
      await fetchProjects()
      selectProject(result.project_id)
      navigate('/workbench')
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Failed to create blank project')
    } finally {
      setCreatingBlank(false)
    }
  }, [blankTitle, blankId, fetchProjects, selectProject, navigate])

  return (
    <div
      style={{
        padding: '1.25rem',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <FileText size={16} style={{ color: 'var(--color-text-secondary)' }} />
        <h3 style={{ margin: 0, fontSize: '1.0125rem', fontWeight: 600 }}>
          Start blank
        </h3>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: '0 0 0.875rem' }}>
        Skip the AI. Create an empty project and add Transactions, Smart Pages, and Dashboards yourself.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
            Title
          </label>
          <input
            type="text"
            value={blankTitle}
            onChange={(e) => setBlankTitle(e.target.value)}
            onBlur={() => {
              if (!blankIdTouched && blankTitle.trim()) {
                setBlankId(slugify(blankTitle))
              }
            }}
            placeholder="My POS App"
            disabled={disabled || creatingBlank}
            style={{
              padding: '0.5rem 0.625rem',
              fontSize: '0.9375rem',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              fontFamily: 'var(--font-primary)',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
            Project ID
          </label>
          <input
            type="text"
            value={blankId}
            onChange={(e) => {
              setBlankId(e.target.value)
              setBlankIdTouched(true)
            }}
            placeholder="my_pos_app"
            disabled={disabled || creatingBlank}
            pattern="[A-Za-z0-9_-]+"
            style={{
              padding: '0.5rem 0.625rem',
              fontSize: '0.9375rem',
              background: 'var(--color-bg)',
              border: `1px solid ${blankId && !blankIdValid ? 'var(--color-error, #dc2626)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              fontFamily: 'var(--font-mono, monospace)',
            }}
          />
        </div>
      </div>
      {blankId && !blankIdValid && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-error, #dc2626)', margin: '0 0 0.5rem' }}>
          Use only letters, digits, _ or -.
        </p>
      )}
      <button
        className="btn btn-secondary"
        onClick={handleCreateBlank}
        disabled={disabled || creatingBlank || !blankTitle.trim() || !blankId.trim() || !blankIdValid}
      >
        {creatingBlank ? (
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <FileText size={16} />
        )}
        {creatingBlank ? 'Creating...' : 'Create blank project'}
      </button>
    </div>
  )
}
